import { RailScroller } from "@/components/home/RailScroller";
import { RAIL_COLUMN, SectionRail } from "@/components/home/SectionRail";
import { Heading } from "@/components/ui";
import { ComboSavingsCard } from "@/components/suggestions/ComboSavingsCard";
import { SuggestionCard } from "@/components/suggestions/SuggestionCard";
import type { ComboSavings, EventSurface, RenderableSuggestion } from "@/domain/suggestions";
import { getSuggestionEventsSink } from "@/domain/suggestions";
import { DEFAULT_STOREFRONT_APPEARANCE, STOREFRONT_TITLE_REM } from "@/domain/storefront-appearance";
import { useStorefrontAppearance } from "@/hooks/use-storefront-appearance";
import { useEffect, useRef, useState } from "react";

const IMPRESSION_THRESHOLD = 0.5;

/**
 * Shared horizontal strip for the three suggestion surfaces (cart upsell,
 * item upsell, home daypart). Renders NOTHING when there is nothing to show
 * — no skeleton, no reserved height — so it can never shift the checkout
 * button or delay first paint.
 *
 * Records one `IMPRESSION` (per session, per surface+anchor-set — the sink
 * itself dedupes) once at least half the strip is on screen, and announces
 * "Adicionado" politely for screen-reader users after a tap.
 */
export function SuggestionsStrip({
  merchantId,
  surface,
  title,
  suggestions,
  combo,
  onAdd,
  onOpenDetail,
  onOpenCombo,
  anchorItemId,
}: {
  merchantId: string | null;
  surface: EventSurface;
  title: string;
  suggestions: RenderableSuggestion[];
  combo: ComboSavings | null;
  onAdd: (suggestion: RenderableSuggestion) => void;
  onOpenDetail: (suggestion: RenderableSuggestion) => void;
  onOpenCombo: (comboItemOfferId: string) => void;
  /** The viewed item on the `ITEM` surface — tags the IMPRESSION event's top-level anchor. */
  anchorItemId?: string;
}) {
  const appearance = useStorefrontAppearance();
  const sectionRef = useRef<HTMLElement>(null);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    if (!merchantId || suggestions.length === 0) return;
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        getSuggestionEventsSink(merchantId).enqueue({
          type: "IMPRESSION",
          surface,
          itemIds: suggestions.map((suggestion) => suggestion.merchantItemId),
          ...(anchorItemId ? { anchorItemId } : {}),
          items: suggestions.map((suggestion) => ({
            itemId: suggestion.merchantItemId,
            arm: suggestion.arm,
            anchorItemId: suggestion.anchorItemId,
          })),
        });
        observer.disconnect();
      },
      { threshold: IMPRESSION_THRESHOLD }
    );
    observer.observe(section);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, surface, anchorItemId, suggestions.map((s) => s.merchantItemId).join(",")]);

  if (suggestions.length === 0) return null;

  function handleAdd(_event: React.MouseEvent<HTMLButtonElement>, suggestion: RenderableSuggestion) {
    onAdd(suggestion);
    setAnnouncement("");
    requestAnimationFrame(() => setAnnouncement(`Adicionado ${suggestion.name}`));
  }
  const cards = (
    <ul className="flex gap-3 pb-1 snap-x">
      {suggestions.map((suggestion) => (
        <SuggestionCard
          key={suggestion.merchantItemId}
          suggestion={suggestion}
          onAdd={handleAdd}
          onOpenDetail={onOpenDetail}
        />
      ))}
      {combo && <ComboSavingsCard combo={combo} onOpen={onOpenCombo} />}
    </ul>
  );

  const body = (
    <>
      <RailScroller
        className={surface === "DAYPART" ? RAIL_COLUMN : undefined}
        watch={suggestions.length + (combo ? 1 : 0)}
      >
        {cards}
      </RailScroller>
      <span role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </>
  );

  if (surface === "DAYPART" || surface === "CART") {
    // Suggestions have no separate appearance settings; follow the bestsellers heading.
    const bestsellers =
      appearance.sections.find((section) => section.key === "bestsellers") ??
      DEFAULT_STOREFRONT_APPEARANCE.sections.find((section) => section.key === "bestsellers")!;
    return (
      <SectionRail
        label={title}
        titleCard={bestsellers.titleCard}
        sectionRef={sectionRef}
        columnClassName={surface === "CART" ? "w-full" : undefined}
      >
        {body}
      </SectionRail>
    );
  }

  return (
    <section ref={sectionRef} aria-labelledby={`suggestions-${surface}-title`} className="pt-6">
      <Heading
        id={`suggestions-${surface}-title`}
        clamp={1}
        className="mb-3"
        title={title}
        style={{ fontSize: `${STOREFRONT_TITLE_REM[appearance.titleSize]}rem` }}
      >
        {title}
      </Heading>
      {body}
    </section>
  );
}
